import React, { useState } from 'react';
import { Phone, Building2, ArrowRight, CheckCircle2, X, Shield, AlertCircle, DollarSign, Clock, Percent } from 'lucide-react';
import { ExternalLink, Info, ChevronDown, Star, Zap, Calendar, CreditCard, CheckCircle, Tag } from 'lucide-react';

interface SponsoredBank {
  id: string;
  name: string;
  logo: string;
  type: string;
  amount: string;
  term: string;
  rate: string;
  color: string;
  description: string;
  features: string[];
  tags?: string[];
  notes?: string[];
  isNew?: boolean;
  processingTime?: string;
  monthlyPayment?: string;
  totalPayment?: string;
}

const sponsoredBanks: SponsoredBank[] = [
  {
    id: 'akbank',
    name: 'AKBANK',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/7accd65f-06ae-48b1-875e-6d9827590be6.png',
    type: 'İhtiyaç Kredisi',
    amount: '75.000 TL',
    term: '3 Ay',
    rate: '0',
    color: 'green',
    description: '3 ay vadeli, 50.000 TL ihtiyaç kredisi! 3 ay vadeli, 25.000 TL taksitli nakit avans!',
    features: [
      'Faizsiz kredi imkanı',
      'Hızlı onay süreci',
      'Masrafsız kullanım'
    ],
    tags: ['Sponsorlu', 'Yeni Müşterilere Özel'],
    processingTime: '5 dakika',
    isNew: true
  },
  {
    id: 'qnb',
    name: 'QNB',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png',
    type: 'İhtiyaç Kredisi',
    amount: '75.000 TL',
    term: '3 Ay',
    rate: '0',
    color: 'purple',
    description: '3 ay vadeli, 50.000 TL ihtiyaç kredisi! 3 ay vadeli, 25.000 TL taksitli nakit avans!',
    features: [
      'Faizsiz kredi imkanı',
      'Hızlı onay süreci',
      'Masrafsız kullanım'
    ],
    tags: ['Sponsorlu', 'Yeni Müşterilere Özel'],
    processingTime: '3 dakika',
    isNew: true
  },
  {
    id: 'garanti',
    name: 'Garanti BBVA',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png',
    type: 'İhtiyaç Kredisi',
    amount: '75.000 TL',
    term: '3 Ay',
    rate: '0',
    color: 'green',
    description: '3 ay vadeli, 50.000 TL\'ye varan kredi! 3 ay vadeli, 25.000 TL\'ye varan taksitli nakit avans!',
    features: [
      'Faizsiz kredi imkanı',
      'Hızlı onay süreci',
      'Masrafsız kullanım'
    ],
    tags: ['Sponsorlu', 'Yeni Müşterilere Özel'],
    processingTime: '4 dakika',
    isNew: true
  },
  {
    id: 'isbank',
    name: 'İş Bankası',
    logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png',
    type: 'İhtiyaç Kredisi',
    amount: '55.000 TL',
    term: '3 Ay',
    rate: '0',
    color: 'blue',
    description: '3 ay vadeli 25.000 TL\'ye varan Taksitli Nakit Avans! 1 ay vadeli 30.000 TL\'ye varan Ek Hesap!',
    features: [
      'Faizsiz kredi imkanı',
      'Taksitli nakit avans',
      'Ek hesap imkanı'
    ],
    tags: ['Sponsorlu', 'Yeni Müşterilere Özel'],
    processingTime: '3 dakika',
    isNew: true
  }
];

const SponsoredBanks = () => {
  const [expandedNotes, setExpandedNotes] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      {sponsoredBanks.map((bank) => (
        <div 
          key={bank.id}
          className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-[#F7BA45]/50 transition-all duration-300 hover:shadow-lg"
        >
          <div className="p-4 md:p-6">
            {/* Bank Header */}
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <div className="flex items-center gap-3 md:gap-4">
                <img 
                  src={bank.logo} 
                  alt={bank.name} 
                  className="h-8 md:h-12 object-contain"
                />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg md:text-xl font-semibold text-slate-800">{bank.name}</h3>
                    {bank.tags?.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#F7BA45]/10 text-[#F7BA45] text-xs font-medium"
                      >
                        <Tag size={12} />
                        {tag}
                      </span>
                    ))}
                    {bank.isNew && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 text-xs font-medium">
                        <Zap size={12} />
                        Yeni
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs md:text-sm text-slate-600 mt-1">
                    <Clock size={14} className="text-[#F7BA45]" />
                    <span>Onay Süresi: {bank.processingTime}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bank Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
              <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                <div className="text-xs md:text-sm text-slate-500 mb-1">Kredi Tutarı</div>
                <div className="font-semibold text-slate-800 text-sm md:text-base">{bank.amount}</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                <div className="text-xs md:text-sm text-slate-500 mb-1">Vade</div>
                <div className="font-semibold text-slate-800 text-sm md:text-base">{bank.term}</div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                <div className="text-xs md:text-sm text-slate-500 mb-1">Faiz Oranı</div>
                <div className="font-semibold text-slate-800 text-sm md:text-base">
                  {bank.rate === '0' ? '%0 (Faizsiz)' : `%${bank.rate}`}
                </div>
              </div>
              <div className="bg-slate-50 rounded-xl p-3 md:p-4">
                <div className="text-xs md:text-sm text-slate-500 mb-1">
                  {bank.id === 'akbank' || bank.id === 'qnb' || bank.id === 'garanti' || bank.id === 'isbank' ? 'Toplam' : 'Aylık Taksit'}
                </div>
                <div className="font-semibold text-slate-800 text-sm md:text-base">
                  {bank.id === 'akbank' || bank.id === 'qnb' || bank.id === 'garanti' || bank.id === 'isbank' ? bank.amount : bank.monthlyPayment || '4.771 TL'}
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-4 md:mb-6">
              {bank.features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-2 text-sm text-slate-600"
                >
                  <CheckCircle size={16} className="text-[#F7BA45]" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            {/* Bank Actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs md:text-sm text-slate-600">
                <Info size={16} className="text-[#F7BA45]" />
                <span>
                  {bank.id === 'akbank' 
                    ? 'Başvuru için bankalara yönlendirilirsiniz' 
                    : `Başvuru için ${bank.name}'a yönlendirilirsiniz`}
                </span>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <a href="#" className="text-xs md:text-sm text-[#F7BA45] hover:underline whitespace-nowrap">
                  {bank.id === 'akbank' || bank.id === 'qnb' || bank.id === 'garanti' || bank.id === 'isbank' ? 'Koşullar' : 'Detaylı Bilgi'}
                  <ExternalLink size={12} className="inline-block ml-1" />
                </a>
                <button className="flex-1 sm:flex-none bg-[#F7BA45] hover:bg-[#e5ac3d] text-slate-900 px-4 md:px-6 py-2.5 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-lg text-sm md:text-base">
                  HEMEN BAŞVUR
                  <ExternalLink size={16} className="hidden sm:block" />
                </button>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          {bank.notes && (
            <div className="border-t border-slate-200">
              <button
                onClick={() => setExpandedNotes(expandedNotes === bank.id ? null : bank.id)}
                className="w-full px-4 md:px-6 py-3 flex items-center justify-between text-sm font-medium text-[#F7BA45] hover:text-[#e5ac3d] transition-colors"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} />
                  <span>Önemli Notlar</span>
                </div>
                <ChevronDown 
                  size={18} 
                  className={`transition-transform duration-300 ${
                    expandedNotes === bank.id ? 'rotate-180' : ''
                  }`} 
                />
              </button>
              
              <div 
                className={`transition-all duration-300 overflow-hidden ${
                  expandedNotes === bank.id ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-4 md:px-6 py-4 bg-slate-50 space-y-2">
                  {bank.notes.map((note, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <AlertCircle size={16} className="flex-shrink-0 mt-1 text-amber-500" />
                      <p className="text-sm text-slate-600">{note}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SponsoredBanks;